<template>
    <van-popup
        :position="position"
        :style="popupStyle"
        :overlay-style="overlayStyle"
        :z-index="zIndex"
        @close="onClose"
    >
        <div class="location-modal">
            <!-- 面包屑导航 -->
            <div class="breadcrumb">
                <div class="breadcrumb-inner">
                    <div
                        v-for="(item, index) in state.breadcrumb"
                        :key="index"
                        :class="[
                            'breadcrumb-item',
                            { active: index === state.breadcrumb.length - 1 },
                        ]"
                        @click="onBreadcrumbTap(index)"
                    >
                        {{ item.name }}
                    </div>
                </div>
            </div>
            <!-- 树形数据列表 -->
            <div class="location-list" scroll-y>
                <div class="location-item-group" v-if="!isMultiple">
                    <div
                        v-for="(item, index) in state.currentLevelData"
                        :key="index"
                        :class="[
                            'location-item',
                            {
                                selectable:
                                    operableUuids.indexOf(item.uuid) !== -1,
                            },
                            {
                                disabled:
                                    operableUuids.indexOf(item.uuid) === -1,
                            },
                            {
                                active:
                                    state.selDepartmentInfo.uuid == item.uuid,
                            },
                        ]"
                        @click="selectLocation(item)"
                    >
                        <div class="item-name">
                            {{ item.name }}
                        </div>
                        <div
                            class="item-arrow"
                            v-if="item.children && item.children.length > 0"
                        >
                            <van-icon name="arrow" size="16px" color="#999" />
                        </div>
                    </div>
                </div>
                <div class="location-item-group" v-if="isMultiple">
                    <div
                        class="location-item-block"
                        v-for="(item, index) in state.currentLevelData"
                        :key="index"
                    >
                        <van-checkbox
                            :checked="
                                localDepartmentUuid.indexOf(item.uuid) !== -1
                            "
                            custom-class="checkbox"
                            label-class="label-class"
                            :disabled="operableUuids.indexOf(item.uuid) === -1"
                            :class="'checkboxes-' + index"
                            :name="item.uuid"
                            @click="onCheckChange(item)"
                        >
                        </van-checkbox>
                        <div
                            :class="[
                                'location-item',
                                operableUuids.includes(item.uuid)
                                    ? 'selectable'
                                    : 'disabled',
                                {
                                    active:
                                        state.selDepartmentInfo.uuid ===
                                        item.uuid,
                                },
                            ]"
                            @click="selectLocation(item)"
                        >
                            <div class="item-name">{{ item.name }}</div>
                            <div
                                class="item-arrow"
                                v-if="item.children && item.children.length > 0"
                            >
                                <van-icon
                                    name="arrow"
                                    size="16px"
                                    color="#999"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 底部按钮 -->
            <div class="modal-footer">
                <van-button
                    color="#999"
                    plain
                    size="large"
                    @click="goBackLevel"
                    :disabled="state.breadcrumb.length <= 1"
                >
                    返回上级
                </van-button>
                <van-button
                    v-if="!isMultiple"
                    type="primary"
                    size="large"
                    @click="confirmLocation"
                    :disabled="!state.canConfirm"
                >
                    确认
                </van-button>
                <van-button
                    v-if="isMultiple"
                    type="primary"
                    size="large"
                    @click="confirmLocation"
                    :disabled="!localDepartmentUuid.length"
                >
                    确认
                </van-button>
            </div>
        </div>
    </van-popup>
</template>
<script setup>
import {
    getDepartmentListAll,
    getDeptAndPost,
    setDefaultDepartment,
} from "@/utils/common";
import { ref, reactive, computed, watch, nextTick } from "vue";
import { showToast } from "vant";
import { useDepartmentStore } from "@/stores";
import activityApi from "@/utils/api/activity.js";

const props = defineProps({
    visible: { type: Boolean, default: false },
    position: { type: String, default: "bottom" },
    treeData: { type: Array, default: () => [] },
    operableUuids: { type: Array, default: () => [] },
    top: { type: String, default: "none" },
    height: { type: Number, default: 0 },
    overlayTop: { type: Number, default: 0 },
    zIndex: { type: Number, default: 10 },
    selectedItem: { type: Object, default: null },
    departmentUuid: { type: Array, default: () => [] },
    multipleItem: { type: Array, default: () => [] },
    isMultiple: { type: Boolean, default: false },
    type: { type: String, default: "department" },
});
const emit = defineEmits(["confirm", "close", "update:show"]);

const departmentStore = useDepartmentStore();

// 响应式状态对象（使用 reactive 替代多个 ref）
const state = reactive({
    departmentInfo: [], // 部门列表
    selDepartmentInfo: {}, // 当前选择部门信息
    selectedLocation: "",
    showLocationModal: false,
    currentLevelData: [],
    breadcrumb: [],
    canConfirm: false,
    checked: false,
});
// 计算属性：弹窗样式
const popupStyle = computed(() => {
    // 将 rpx 转换为 px（假设 1rpx = 0.5px，根据项目配置调整）
    const rpxToPx = (rpx) => {
        if (typeof rpx === "number") return rpx * 0.5;
        return parseInt(rpx) * 0.5;
    };
    let style = {
        top: props.top !== "none" ? `${rpxToPx(props.top)}px` : undefined,
        height: props.height
            ? `calc(100% - ${rpxToPx(props.height)}px)`
            : undefined,
    };
    return style;
});

// 计算属性：遮罩层样式
const overlayStyle = computed(() => {
    const rpxToPx = (rpx) => {
        if (typeof rpx === "number") return rpx * 0.5;
        return parseInt(rpx) * 0.5;
    };

    return {
        top: `${rpxToPx(props.overlayTop)}px`,
        opacity: 0.5,
    };
});

// 多选相关状态（单独管理，因为需要响应式更新）
const localDepartmentUuid = ref([...props.departmentUuid]);
const localMultipleItem = ref([...props.multipleItem]);

// 计算属性
const visible = computed({
    get: () => props.show,
    set: (val) => emit("update:show", val),
});

const selectorStyle = computed(() => {
    const style = {};
    if (props.height) {
        style.height = `${props.height}rpx`;
    }
    if (props.top !== "none") {
        style.marginTop = `${props.top}rpx`;
    }
    return style;
});

const isDisabled = (uuid) => !props.operableUuids.includes(uuid);

const isChecked = (uuid) => localDepartmentUuid.value.includes(uuid);

// 监听 show 变化
watch(
    () => props.visible,
    (newVal) => {
        if (newVal && props.treeData.length) {
            nextTick(() => init());
        }
    },
    { immediate: true }
);

// 初始化
const init = () => {
    const { treeData, selectedItem } = props;

    let newBreadcrumb = [];
    let newCurrentLevelData = treeData;
    let newSelectedItem = null;

    const findByName = (data, uuid, path = []) => {
        for (const item of data) {
            if (item.uuid === uuid) {
                return { item, path: [...path, item] };
            }
            if (item.children?.length > 0) {
                const result = findByName(item.children, uuid, [...path, item]);
                if (result) return result;
            }
        }
        return null;
    };

    const result = findByName(treeData, selectedItem?.uuid);

    if (result) {
        const { item, path } = result;

        if (item.type !== 2) {
            newBreadcrumb.push(...path);
        } else {
            newBreadcrumb.push(...path.slice(0, path.length - 1));
        }

        const lastItem = newBreadcrumb[newBreadcrumb.length - 1];
        newCurrentLevelData = lastItem?.children || [];
        newSelectedItem = item;
    }

    // 直接修改 state 属性
    state.breadcrumb = newBreadcrumb;
    state.currentLevelData = newCurrentLevelData;

    if (newSelectedItem) {
        state.selDepartmentInfo = departmentStore.setDefaultDepartment(
            newSelectedItem,
            props.type
        );
    }

    state.canConfirm = !!newSelectedItem;
};

// 选择部门
const selectLocation = (item) => {
    // const index = props.operableUuids.indexOf(item.uuid);
    // if (props.isMultiple && index !== -1) {
    //     onCheckChange(item);
    //     return false;
    // }
    // if (isDisabled(item.uuid)) {
    //     showToast({ message: "无操作权限" });
    //     return;
    // }

    const hasOperable = props.operableUuids.includes(item.uuid);
    if (item.children && item.children?.length > 0) {
        state.breadcrumb.push({ uuid: item.uuid, name: item.name });
        state.currentLevelData = item.children;
        state.selDepartmentInfo = hasOperable ? item : {};
        props.selectedItem = hasOperable ? item : {};
        state.canConfirm = hasOperable;
    } else {
        state.selDepartmentInfo = hasOperable ? item : {};
        props.selectedItem = hasOperable ? item : {};
        state.canConfirm = hasOperable;
    }
};

// 多选切换
const onCheckChange = (item) => {
    const index = localDepartmentUuid.value.indexOf(item.uuid);

    if (index !== -1) {
        localDepartmentUuid.value.splice(index, 1);
        localMultipleItem.value.splice(index, 1);
    } else {
        localDepartmentUuid.value.push(item.uuid);
        localMultipleItem.value.push(item);
    }
    console.log(localDepartmentUuid.value);
    console.log(localMultipleItem.value);
};

// 面包屑导航点击
const onBreadcrumbTap = (index) => {
    return false;
    const { treeData, operableUuids } = props;
    const bc = state.breadcrumb;

    let newBreadcrumb = [];
    let newCurrentLevelData = [];
    let foundItem = null;
    let selIndex = -1;

    if (index === 0) {
        selIndex = operableUuids.indexOf(bc[0].uuid);
        newCurrentLevelData = bc[0].children || [];
        newBreadcrumb = [bc[0]];
        foundItem = bc[0];
    } else {
        let currentData = treeData[0]?.children || [];

        for (let i = 1; i <= index; i++) {
            const targetId = bc[i].uuid;
            foundItem = currentData.find((d) => d.uuid === targetId);
            selIndex = operableUuids.indexOf(foundItem?.uuid);

            if (foundItem) {
                if (i < index && foundItem.children) {
                    currentData = foundItem.children;
                } else if (i === index) {
                    newCurrentLevelData = foundItem.children || [];
                }
            } else {
                break;
            }
        }

        newBreadcrumb = bc.slice(0, index + 1);
    }
    state.breadcrumb = newBreadcrumb;
    state.currentLevelData = newCurrentLevelData;
    state.selDepartmentInfo = selIndex !== -1 ? foundItem : {};
    props.selectedItem = selIndex !== -1 ? foundItem : {};
    state.canConfirm = selIndex !== -1;
};

// 返回上一级
const goBackLevel = () => {
    const { treeData, operableUuids } = props;
    const bc = state.breadcrumb;

    let newBreadcrumb = [];
    let newCurrentLevelData = treeData[0]?.children || [];
    let item = null;
    let selIndex = -1;

    if (bc.length <= 1) {
        newBreadcrumb = bc;
        item = treeData[0];
        selIndex = operableUuids.indexOf(item?.uuid);
        newCurrentLevelData = item?.children || [];
    } else {
        newBreadcrumb = bc.slice(0, -1);

        for (let i = 1; i < newBreadcrumb.length; i++) {
            item = newCurrentLevelData.find(
                (d) => d.uuid === newBreadcrumb[i].uuid
            );
            selIndex = operableUuids.indexOf(item?.uuid);
            if (item?.children) {
                newCurrentLevelData = item.children;
            }
        }

        if (newBreadcrumb.length === 1) {
            item = treeData[0];
            selIndex = operableUuids.indexOf(item?.uuid);
            newCurrentLevelData = newCurrentLevelData || [];
        }
    }
    state.breadcrumb = newBreadcrumb;
    state.currentLevelData = newCurrentLevelData;
    state.selDepartmentInfo = selIndex !== -1 ? item : {};
    props.selectedItem = selIndex !== -1 ? item : {};
    state.canConfirm = selIndex !== -1;
};

// 确认选择
const confirmLocation = () => {
    const { isMultiple } = props;

    if (!isMultiple && !state.selDepartmentInfo?.uuid) {
        showToast({ message: "请选择位置", icon: "none" });
        return;
    }

    if (
        !isMultiple &&
        !props.operableUuids.includes(state.selDepartmentInfo.uuid)
    ) {
        showToast({ message: "该位置无操作权限", icon: "none" });
        return;
    }

    if (!isMultiple) {
        emit("confirm", { ...state.selDepartmentInfo });
    } else {
        emit("confirm", {
            departmentUuid: localDepartmentUuid.value,
            multipleItem: localMultipleItem.value,
        });
    }

    onClose();
};

// 关闭弹窗
const onClose = () => {
    visible.value = false;
    const { isMultiple } = props;
    if (!isMultiple) {
        state.selDepartmentInfo = {};
        props.selectedItem = {};
    } else {
        localDepartmentUuid.value = [];
        localMultipleItem.value = [];
    }
    emit("close");
};
</script>
<style lang="less" scoped>
:host {
}

/* 位置选择弹窗 */
/* 弹窗容器 */
.location-modal {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #f9f9f9;
}

/* 面包屑导航 */
.breadcrumb {
    padding: 18px 17px;
    min-height: 60px;
    border-bottom: 1px solid #f0f0f0;
    background: #ffffff;
}

.breadcrumb-inner {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 11px;
}

.breadcrumb-item {
    position: relative;
    font-size: 15px;
    color: #666;
    line-height: 21rpx;
    /* padding: 4px 8px; */
    display: flex;
    align-items: center;
}

.breadcrumb-item::after {
    /* position: absolute; */
    /* top: 50%; */
    right: 0;
    /* transform: translateY(-50%); */
    content: "＞";
    height: 21px;
    line-height: 21px;
    margin-left: 11px;
    color: #666;
}

.breadcrumb-item:last-child::after {
    content: "";
    margin-left: 0;
}

.breadcrumb-item.active {
    color: #217fff;
    font-weight: 600;
}

/* 列表区域 */
.location-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;
    height: 400px;
}

.location-item-group {
    margin: 9px 17px;
    padding: 3px 10px;
    background-color: #fff;
    border-radius: 8px;
}

.location-item-block {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.location-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 8px;
    width: 100%;
    border-bottom: 2px solid #ececec;
    transition: background-color 0.2s;
}
.location-item-block .location-item {
    width: 95%;
}

.label-class {
    width: 100%;
}

.location-item-group .location-item:last-child {
    border-bottom: none;
}

.location-item.selectable {
    background: white;
}

.location-item.active {
    background-color: rgba(33, 127, 255, 0.2);
    color: #fff;
}

.location-item.disabled {
    opacity: 0.5;
}

.item-name {
    font-size: 14px;
    color: #333;
    flex: 1;
}

.item-arrow {
    margin-left: 8px;
    color: #999;
    display: flex;
}

/* 底部操作栏 */
.modal-footer {
    padding: 12px 16px 24px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    gap: 12px;
    background-color: #fff;
}

.modal-footer van-button {
    flex: 1;
}
</style>
